/**
 * Consistent colour mapping for the 7 AI risk domains used in Incident Tracker charts.
 * Colours chosen to match the screenshot reference and maintain visual distinction.
 */

export const DOMAIN_COLORS: Record<string, string> = {
  '1 Discrimination & Toxicity': '#377eb8',       // blue
  '2 Privacy & Security': '#ff7f00',              // orange
  '3 Misinformation': '#4daf4a',                  // green
  '4 Malicious actors': '#e41a1c',                // red
  '5 Human-Computer Interaction': '#a65628',       // brown
  '6 Socioeconomic & Environmental': '#f781bf',    // pink
  '7 AI system safety, failures, & limitations': '#a6cee3', // light blue/cyan
};

/** Ordered list of domain names matching canonical sort order */
export const DOMAIN_ORDER = [
  '1 Discrimination & Toxicity',
  '2 Privacy & Security',
  '3 Misinformation',
  '4 Malicious actors',
  '5 Human-Computer Interaction',
  '6 Socioeconomic & Environmental',
  '7 AI system safety, failures, & limitations',
];

/**
 * Get colour for a domain name. Falls back to grey for unknown domains.
 */
export function getDomainColor(domain: string): string {
  return DOMAIN_COLORS[domain] || '#999999';
}

/**
 * Subdomain colours: derived from parent domain colour with slight variation.
 * We use the parent domain colour for each subdomain since there are many subdomains
 * and the parent domain colour provides visual grouping.
 */
export function getSubdomainColor(subdomain: string): string {
  // Extract domain number from subdomain string like "1.1 Unfair discrimination..."
  const domainNum = subdomain.match(/^(\d+)\./)?.[1];
  if (!domainNum) return '#999999';

  const domainName = DOMAIN_ORDER.find((d) => d.startsWith(`${domainNum} `));
  if (!domainName) return '#999999';

  return DOMAIN_COLORS[domainName] || '#999999';
}

/**
 * Extended subdomain palette: gives each subdomain within a domain a slightly
 * different shade by adjusting lightness. Used for charts that need to distinguish
 * individual subdomains (e.g., subdomain-by-year stacked bar).
 */
const SUBDOMAIN_PALETTE: Record<string, string[]> = {
  '1': ['#377eb8', '#6aa4d0', '#9dc9e8'],                           // blues
  '2': ['#ff7f00', '#ffaa4d'],                                       // oranges
  '3': ['#4daf4a', '#7cc47a', '#e41a1c'],                            // green + red for pollution
  '4': ['#f781bf', '#984ea3', '#b07cc4'],                            // pink/purple
  '5': ['#a65628', '#c8865e'],                                       // browns
  '6': ['#f781bf', '#e86baf', '#d44f9f', '#c03090', '#6f9b3b', '#bfb535'], // pinks + misc
  '7': ['#17becf', '#7fcdbb', '#41b6c4', '#1d91c0'],                // cyans/teals
};

export function getSubdomainPaletteColor(subdomain: string, indexInDomain: number): string {
  const domainNum = subdomain.match(/^(\d+)\./)?.[1];
  if (!domainNum) return '#999999';
  const palette = SUBDOMAIN_PALETTE[domainNum];
  if (!palette) return '#999999';
  return palette[indexInDomain % palette.length];
}
